import React, { useState, useEffect, useRef } from "react";
import { cn } from "tailwind-variants";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import {
  trackRudderstackImageEdited,
  trackRudderstackImageUploadError
} from "common/utils/rudderstack/tracker.react";
import { getStickerDimensions } from "common/utils/stickers";
import UAParser from "ua-parser-js";
import isEqual from "lodash.isequal";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import StepNumber from "./common/StepNumber.vue";
import { useSpanglesStore, SPANGLE_MODES } from "./spangles-module";
import { useI18n } from "intl/useI18n";
import { useDtfStore } from "./store";
import {
  MIN_DIMENSION_SIZE,
  MIN_DPI,
  PRINT_AS_IS_MIN_DPI,
  PRINT_AS_IS_MAX_HEIGHT,
  PRINT_AS_IS_MIN_WIDTH,
  MAX_SIZE_FIELD_VALUE,
  UPSCALE_THRESHOLD,
  DEFAULT_VALUES,
  GANG_DEFAULT_VALUES,
  MIN_DIMENSION_PUFF
} from "./common/constants";
import "./ArtworkDimensions.scss";

export default function ArtworkDimensions({
  artwork = null,
  index = 0,
  disabled = false,
  readonly = false,
  widthDefault = null,
  currentStore = null,
  isMultiUpload = false,
  ...restProps
}) {
  const { n } = useI18n();

  const [newWidth, setNewWidth] = useState(null);
  const [newHeight, setNewHeight] = useState(null);
  const [oldWidth, setOldWidth] = useState(null);
  const [oldHeight, setOldHeight] = useState(null);
  const [isHeightValid, setIsHeightValid] = useState(true);
  const [isWidthValid, setIsWidthValid] = useState(true);
  const [previousData, setPreviousData] = useState({
    width: null,
    height: null
  });
  const [sizeChanged, setSizeChanged] = useState(false);

  const store = useDtfStore();
  const spanglesStore = useSpanglesStore();
  const prevArtworkRef = useRef(artwork);

  function variantId() {
    return store.variantId;
  }

  function simulation() {
    return store.simulation;
  }

  function printAsIs() {
    return store.printAsIs;
  }

  function isPuff() {
    return store.isPuff;
  }

  function pdpType() {
    return store.pdpType;
  }

  function catalogNumber() {
    return store.catalogNumber;
  }

  function dpiInstructionsUrl() {
    return store.dpiInstructionsUrl;
  }

  function showModal() {
    return store.showModal;
  }

  function dtfMaxWidth() {
    return store.dtfMaxWidth;
  }

  function dtfMaxHeight() {
    return store.dtfMaxHeight;
  }

  function dtfMinWidth() {
    return store.dtfMinWidth;
  }

  function dtfMinHeight() {
    return store.dtfMinHeight;
  }

  function errorTypes() {
    return store.errorTypes;
  }

  function isSpangle() {
    return store.isSpangle;
  }

  function sticker() {
    return store.sticker;
  }

  function businessLine() {
    return store.businessLine;
  }

  function showStepsUI() {
    return store.showStepsUI;
  }

  function isStandardArtwork() {
    return store.isStandardArtwork;
  }

  function isProColor() {
    return store.isProColor;
  }

  function artworks() {
    return store.artworks;
  }

  function preCut() {
    return store.preCut;
  }

  function spangleSizeEdited() {
    return spanglesStore.spangleSizeEdited;
  }

  function isInitialSpangle() {
    return spanglesStore.isInitialSpangle;
  }

  function validationError() {
    return artwork?.errorMessage;
  }

  function validationErrorType() {
    return artwork?.errorType;
  }

  function gangPadlock() {
    return printAsIs() && !showModal();
  }

  function lockHeight() {
    return gangPadlock() && !!metadata();
  }

  function readonlyHeight() {
    if (showModal() && printAsIs()) return readonly;
    if (!lockHeight()) return false;
    return readonly;
  }

  function minDpi() {
    if (printAsIs()) return PRINT_AS_IS_MIN_DPI;
    return metadata()?.upscalingEnabled ? Number(metadata().qualityUpscaleDpi) : MIN_DPI;
  }

  function pricing() {
    return artwork?.pricing || simulation();
  }

  function priceInch() {
    if (pricing()?.pricePerInch) {
      return Number(pricing().pricePerInch).toFixed(4);
    }
    return null;
  }

  function totalPrice() {
    if (pricing()?.transferPrice) {
      return Number(pricing().transferPrice).toFixed(2) || "";
    }
    return null;
  }

  function widthId() {
    return `artwork[${index}][variants][${variantId()}][width]`;
  }

  function heightId() {
    return `artwork[${index}][variants][${variantId()}][height]`;
  }

  function metadata() {
    const { removeBackground, metadata: artworkMetadata, withBgMetadata } = artwork || {};
    if (!withBgMetadata || Object.keys(withBgMetadata).length === 0) {
      return artworkMetadata;
    }
    return removeBackground ? artworkMetadata : withBgMetadata;
  }

  function width() {
    return artwork?.previewMetadata?.widthInch || artwork?.width || widthDefault;
  }

  function height() {
    const artworkHeight = artwork?.height;
    if (printAsIs() && !!metadata()) {
      return artwork.lengthFoot;
    }
    return artworkHeight;
  }

  function heightLabel() {
    const unit = printAsIs() ? "" : "(inches)";
    const label = printAsIs() ? "Length (feet)" : "Height"; // eslint-disable-line no-shadow
    return showBorderUI() ? label : `${label} ${unit}`;
  }

  function showPaddingNote() {
    return !isSpangle() && !printAsIs() && !isPuff();
  }

  function paddingNote() {
    return sticker()
      ? "Padding for easy peeling is added for free"
      : "Padding for easy cutting is added for free";
  }

  function heightName() {
    return printAsIs() ? "length" : "height";
  }

  function minWidthSetting() {
    return printAsIs() ? PRINT_AS_IS_MIN_WIDTH : dtfMinWidth();
  }

  function isWidthAndHeightValid() {
    return (
      width() !== "" &&
      Number(width()) >= minWidth() &&
      height() !== "" &&
      Number(height()) >= minHeight()
    );
  }

  function fitsGangSheet() {
    return (
      width() !== "" &&
      height() !== "" &&
      Math.min(width(), height()) <= metadata()?.maxSheetWidth &&
      Math.max(width(), height()) <= metadata()?.maxSheetHeight
    );
  }

  function dpi() {
    return Math.round(metadata().width / width());
  }

  function imageMetadataInfo() {
    if (!metadata()) return null;
    return {
      aspect_ratio: metadata().aspectRatio,
      resolution_width: metadata().width,
      resolution_height: metadata().height,
      width: width(),
      height: height(),
      max_width: metadata().maxWidth,
      max_height: metadata().maxHeight,
      max_sheet_width: metadata().maxSheetWidth,
      max_sheet_height: metadata().maxSheetHeight,
      min_dpi: minDpi(),
      file_name: artwork.name,
      file_size: artwork.fileSize,
      file_type: artwork.fileType
    };
  }

  function isUpscaleThresholdExceeded() {
    if (isSpangle()) return false;
    const { originalWidth, originalHeight, width: artworkWidth, height: artworkHeight } = artwork;
    if (!originalWidth || !originalHeight || !artworkWidth || !artworkHeight) return false;
    const newWidth = parseFloat(artworkWidth); // eslint-disable-line no-shadow
    const newHeight = parseFloat(artworkHeight); // eslint-disable-line no-shadow
    const upscaleFactorWidth = (newWidth / originalWidth).toFixed(2);
    const upscaleFactorHeight = (newHeight / originalHeight).toFixed(2);
    const upscaleFactor = Math.min(upscaleFactorWidth, upscaleFactorHeight);
    return upscaleFactor > UPSCALE_THRESHOLD;
  }

  function showSpangleEditSize() {
    return isSpangle() && !isInitialSpangle();
  }

  function minSizeForProduct() {
    if (
      width() === null ||
      width() === undefined ||
      width() === "" ||
      height() === null ||
      height() === undefined ||
      height() === ""
    ) {
      return true;
    }
    const widthNum = Number(width());
    const heightNum = Number(height());
    if (Number.isNaN(widthNum) || Number.isNaN(heightNum)) {
      return true;
    }
    if (isSpangle()) {
      return widthNum >= dtfMinWidth() && heightNum >= dtfMinHeight();
    }
    if (sticker()) {
      return widthNum >= MIN_DIMENSION_SIZE && heightNum >= MIN_DIMENSION_SIZE;
    }
    if (isPuff()) {
      return widthNum >= MIN_DIMENSION_PUFF.width && heightNum >= MIN_DIMENSION_PUFF.height;
    }
    return widthNum >= dtfMinWidth() && heightNum >= dtfMinHeight();
  }

  function label() {
    if (isPuff() || sticker()) {
      return "Select size";
    }
    if (isSpangle()) {
      return "Design size";
    }
    return null;
  }

  function isInvalid() {
    return showBorderUI() && validationError() && (!isWidthValid || !isHeightValid);
  }

  function showBorderUI() {
    return showStepsUI() && !isMultiUpload;
  }

  function editType() {
    if (sizeChanged) {
      return "Size Adjustment";
    }
    if (artwork?.enhanceImage) {
      return "Jiffy Print Perfect";
    }
    return "Print as uploaded";
  }

  function minWidth() {
    return isPuff() ? MIN_DIMENSION_PUFF.width : MIN_DIMENSION_SIZE;
  }

  function minHeight() {
    return isPuff() ? MIN_DIMENSION_PUFF.height : MIN_DIMENSION_SIZE;
  }

  function minDimensions() {
    if (isPuff()) {
      return {
        width: MIN_DIMENSION_PUFF.width,
        height: MIN_DIMENSION_PUFF.height
      };
    }
    return {
      width: minWidthSetting(),
      height: dtfMinHeight()
    };
  }

  function updateArtworkValues(...args) {
    return store.updateArtworkValues(...args);
  }

  function setValidationErrorMessage(...args) {
    return store.setValidationErrorMessage(...args);
  }

  function setSpangleMode(...args) {
    return spanglesStore.setSpangleMode(...args);
  }

  function getArtworksPricing(...args) {
    return store.getArtworksPricing(...args);
  }

  function updatePdpDates(...args) {
    return store.updatePdpDates(...args);
  }

  function updatePreviewDpi(...args) {
    return store.updatePreviewDpi(...args);
  }

  function downscaleOriginalImage(...args) {
    return store.downscaleOriginalImage(...args);
  }

  function handleInput(dimensionType, e) {
    let { value } = e.target;
    if (printAsIs() && dimensionType === "height" && value > MAX_SIZE_FIELD_VALUE) {
      value = MAX_SIZE_FIELD_VALUE;
    }
    autoFillInputs(dimensionType, value);
  }

  function handleInputClick(e) {
    e.target.focus();
  }

  function handleFocusIn() {
    setOldWidth(newWidth);
    setOldHeight(newHeight);
  }

  function handleWidthInput(e) {
    handleInput("width", e);
  }

  function handleHeightInput(e) {
    handleInput("height", e);
  }

  function autoFillInputs(field, value) {
    if (!artwork) return;
    let newDimensions = {
      width: artwork.width,
      height: artwork.height
    };
    if (Number(value) > MAX_SIZE_FIELD_VALUE) {
      value = MAX_SIZE_FIELD_VALUE;
    }
    if (field === "width") {
      newDimensions = {
        width: value,
        height: getProportionalEdge(value, "height"),
        widthEdited: true
      };
    }
    if (field === "height") {
      newDimensions = {
        width: getProportionalEdge(value, "width"),
        height: value
      };
    }
    setNewWidth(newDimensions.width);
    setNewHeight(newDimensions.height);
    updateArtworkValues({
      index: artwork.originalIndex,
      ...newDimensions
    });
    updatePreviewDpi({
      artworkId: artwork.id
    });
    downscaleOriginalImage({
      artworkId: artwork.id
    });
  }

  function getProportionalEdge(value, field) {
    if (!metadata()?.aspectRatio) return artwork[field];
    const factor = field === "height" ? 1 / metadata().aspectRatio : metadata().aspectRatio;
    const otherEdge = Math.round(value * factor * 100) / 100;
    return otherEdge.toFixed(2) || null;
  }

  function sendValidationWarning(sendEvent) {
    if (!sendEvent) return;
    const warning = artwork?.metadata?.warnings?.keys[0];
    if (!printAsIs() || !warning) return;
    sendWarningEvent("image_validation_warning", warning);
  }

  function validateValues(sendEvent = false) {
    setValidationError(sendEvent);
    if (!metadata()) {
      setIsHeightValid(isWidthAndHeightValid());
      setIsWidthValid(isWidthAndHeightValid());
      return;
    }
    sendValidationWarning(sendEvent);
    const widthConstraint =
      metadata().aspectRatio > 1 ? metadata().maxSheetHeight : metadata().maxSheetWidth;
    const heightConstraint =
      metadata().aspectRatio > 1 ? metadata().maxSheetWidth : metadata().maxSheetHeight;
    setIsWidthValid(
      fitsGangSheet() ? isWidthAndHeightValid() : width() !== "" && width() <= widthConstraint
    );
    setIsHeightValid(
      fitsGangSheet() ? isWidthAndHeightValid() : height() !== "" && height() <= heightConstraint
    );
  }

  function setValidationError(sendEvent) {
    if (printAsIs() && !artwork?.isSimulated) return;
    const isInvalid = // eslint-disable-line no-shadow
      invalidWidthValidation(sendEvent) ||
      invalidHeightValidation(sendEvent) ||
      negativeValuesValidation(sendEvent) ||
      minPrintSizeValidation(sendEvent) ||
      maxPrintSizeValidation(sendEvent) ||
      maxPrintSizePrintAsIsValidation() ||
      upscaleThresholdExceeded(sendEvent);
    if (!isInvalid) {
      setValidationErrorMessage({
        index,
        message: null,
        type: null
      });
    }
  }

  function invalidWidthValidation(sendEvent = false) {
    if (width() !== "") return false;
    if (validationErrorType() !== errorTypes().INVALID_WIDTH && sendEvent) {
      sendValidationEvent("image_validation_error", "invalid_width", {});
    }
    setValidationErrorMessage({
      index,
      message: "Please enter a valid width.",
      type: errorTypes().INVALID_WIDTH
    });
    return true;
  }

  function invalidHeightValidation(sendEvent = false) {
    if (height() !== "") return false;
    if (validationErrorType() !== errorTypes().INVALID_HEIGHT && sendEvent) {
      sendValidationEvent("image_validation_error", "invalid_height", {});
    }
    setValidationErrorMessage({
      index,
      message: `Please enter a valid ${heightName()}.`,
      type: errorTypes().INVALID_HEIGHT
    });
    return true;
  }

  function negativeValuesValidation(sendEvent = false) {
    if (width() >= 0 && height() >= 0) return false;
    if (validationErrorType() !== errorTypes().NEGATIVE_VALUES && sendEvent) {
      sendValidationEvent("image_validation_error", "negative_values", {
        width: width(),
        height: height()
      });
    }
    setValidationErrorMessage({
      index,
      message: "Negative values are not permitted.",
      type: errorTypes().NEGATIVE_VALUES
    });
    return true;
  }

  function minPrintSizeValidation(sendEvent = false) {
    if (minSizeForProduct()) return false;
    if (validationErrorType() !== errorTypes().MIN_PRINT_SIZE && sendEvent) {
      if (showSpangleEditSize()) {
        setSpangleMode(SPANGLE_MODES.editSize);
      }
      sendValidationEvent("image_validation_error", "min_print_size", {});
    }
    const unit = printAsIs() ? "'" : '"';
    setValidationErrorMessage({
      index,
      message: `The minimum print size is ${minDimensions().width}" x ${minDimensions().height}${unit}`,
      type: errorTypes().MIN_PRINT_SIZE
    });
    return true;
  }

  function maxPrintSizeValidation(sendEvent = false) {
    if (printAsIs() || fitsGangSheet()) return false;
    let maxSheetWidth = dtfMaxWidth();
    let maxSheetHeight = dtfMaxHeight();
    if (metadata() && metadata().maxSheetHeight && metadata().maxSheetWidth) {
      maxSheetWidth =
        metadata().aspectRatio > 1 ? metadata().maxSheetHeight : metadata().maxSheetWidth;
      maxSheetHeight =
        metadata().aspectRatio > 1 ? metadata().maxSheetWidth : metadata().maxSheetHeight;
    }
    if (Number(width()) <= maxSheetWidth && Number(height()) <= maxSheetHeight) {
      return false;
    }
    if (
      sticker() &&
      Number(previousData.width) <= maxSheetWidth &&
      Number(previousData.height) <= maxSheetHeight
    ) {
      return false;
    }
    if (validationErrorType() !== errorTypes().MAX_PRINT_SIZE && sendEvent) {
      if (showSpangleEditSize()) {
        setSpangleMode(SPANGLE_MODES.editSize);
      }
      sendValidationEvent("image_validation_error", "max_print_size", {});
    }
    const message = `The maximum print size is ${maxSheetWidth}" x ${maxSheetHeight}"`;
    setValidationErrorMessage({
      index,
      type: errorTypes().MAX_PRINT_SIZE,
      message
    });
    return true;
  }

  function upscaleThresholdExceeded(sendEvent = false) {
    if (printAsIs() || !isUpscaleThresholdExceeded()) return false;
    if (validationErrorType() !== errorTypes().UPSCALE_THRESHOLD_EXCEEDED && sendEvent) {
      sendValidationEvent("image_validation_error", "upscale_threshold_exceeded", {});
    }
    setValidationErrorMessage({
      index,
      message: "upscale_threshold_exceeded",
      type: errorTypes().UPSCALE_THRESHOLD_EXCEEDED
    });
    return true;
  }

  function maxPrintSizePrintAsIsValidation() {
    if (printAsIs() && height() > (metadata()?.maxSheetHeight || PRINT_AS_IS_MAX_HEIGHT)) {
      setValidationErrorMessage({
        index,
        message: `The maximum print size is ${PRINT_AS_IS_MIN_WIDTH}" x ${PRINT_AS_IS_MAX_HEIGHT}'`,
        type: null
      });
      return true;
    }
    return false;
  }

  function sendValidationEvent(eventType, errorType, data) {
    let metadataObj = {
      ...data,
      has_image: !artwork?.isFile && !artwork?.isSimulated,
      pdp_type: pdpType(),
      business_line: businessLine(),
      page: "pdp"
    };
    if (!artwork?.isFile) {
      metadataObj = {
        ...metadataObj,
        ...imageMetadataInfo()
      };
    }
    submitDtfEvent(eventType, {
      type: errorType,
      ...metadataObj
    });
    trackRudderstackImageUploadError({
      error_message: errorType,
      business_line: businessLine(),
      location: "pdp",
      pre_cut_transfers: preCut()
    });
  }

  function sendWarningEvent(eventType, warningType) {
    const metadataObj = {
      ...imageMetadataInfo(),
      has_image: !artwork?.isFile && !artwork?.isSimulated,
      pdp_type: pdpType(),
      business_line: businessLine(),
      page: "pdp"
    };
    submitDtfEvent(eventType, {
      type: warningType,
      ...metadataObj
    });
  }

  async function updateArtworksPricingAndTrackSizeChange(oldArtworkPrice, newArtworkPrice) {
    const detectProductLine = obj => {
      if (obj.sticker) return "sticker";
      if (obj.isProColor) return "pro color";
      if (obj.isSpangle) return "spangle";
      return "dtf";
    };
    const eventPayload = {
      product_line: detectProductLine({
        sticker: sticker(),
        isProColor: isProColor(),
        isSpangle: isSpangle()
      }),
      business_line: businessLine(),
      artwork,
      edit_type: editType(),
      artworks_count: artworks().length,
      location: "pdp",
      old_price: oldArtworkPrice,
      new_price: newArtworkPrice,
      pre_cut_transfers: preCut()
    };
    setSizeChanged(false);
    trackRudderstackImageEdited(eventPayload);
    store.reindexArtworks();
  }

  function trackSizeChanges() {
    if (newWidth === oldWidth && newHeight === oldHeight) return;
    let sizeChange;
    const area = newWidth * newHeight;
    const oldArea = oldWidth * oldHeight;
    if (area === 0 && oldArea === 0) {
      sizeChange = "none";
    } else if (area > oldArea) {
      sizeChange = "larger";
    } else {
      sizeChange = "smaller";
    }
    const sizePercentChange =
      oldArea === 0 ? 0 : Math.round((Math.abs(area - oldArea) / oldArea) * 100);
    submitDtfEvent("size_modified", {
      width: newWidth,
      height: newHeight,
      previous_width: oldWidth ?? width(),
      previous_height: oldHeight ?? height(),
      size_change: sizeChange,
      business_line: businessLine(),
      size_percent_change: sizePercentChange,
      has_image: !simulation(),
      page: "pdp",
      pdp_type: pdpType()
    });
  }

  function onBlur() {
    const currentData = {
      width: width(),
      height: height()
    };
    if (isEqual(currentData, previousData)) {
      return;
    }
    validateValues(true);
    if (isWidthAndHeightValid()) {
      setPreviousData(currentData);
      getArtworksPricing();
      updatePdpDates();
      trackSizeChanges();
      setSizeChanged(true);
    }
  }

  function openZendeskChat() {
    if (!window.zE) return;
    const device = new UAParser().getDevice();
    if (["mobile", "tablet"].includes(device.type)) return;
    window.zE(() => {
      window.zE("messenger", "open");
    });
  }

  useEffect(() => {
    const dimensions = printAsIs() ? GANG_DEFAULT_VALUES : DEFAULT_VALUES;
    setPreviousData({
      width: dimensions.width,
      height: dimensions.height
    });
  }, []);

  useEffect(() => {
    if (!artwork) return;

    const oldArtwork = prevArtworkRef.current;
    prevArtworkRef.current = artwork;

    if (artwork.isFile || artwork.isSimulated) return;
    if (!!artwork.metadata?.errors && !artwork.isValid) return;

    if (artwork.originalIndex && artwork.originalIndex !== index) {
      updateArtworkValues({
        index: artwork.originalIndex,
        originalIndex: index
      });
      getArtworksPricing();
      return;
    }

    const newArtworkSize = `${artwork.width}${artwork.height}`;
    const oldArtworkSize = `${oldArtwork?.width}${oldArtwork?.height}`;

    const triggerValidateOnSpangleSizeEdit =
      isSpangle() && spangleSizeEdited() && newArtworkSize !== oldArtworkSize;

    const enhancementSwitched =
      isStandardArtwork() && artwork.enhanceImage !== oldArtwork?.enhanceImage;

    if (
      (oldArtwork?.isLoading && !artwork.isLoading) ||
      oldArtwork?.removeBackground !== artwork.removeBackground ||
      triggerValidateOnSpangleSizeEdit ||
      enhancementSwitched
    ) {
      if (sticker()) {
        const skipDefaultCheck = artwork.originalWidth !== artwork.width;
        const newPreviousData = getStickerDimensions(
          artwork.width,
          artwork.height,
          skipDefaultCheck
        );
        setPreviousData(newPreviousData);
        updateArtworkValues({
          index: artwork.originalIndex,
          ...newPreviousData
        });
        getArtworksPricing();
      } else {
        setPreviousData({
          width: artwork.width,
          height: artwork.height
        });
      }
      validateValues(true);
    }

    if (
      artwork.pricing &&
      oldArtwork?.pricing &&
      !isEqual(artwork.pricing.unitPrice, oldArtwork.pricing.unitPrice)
    ) {
      updateArtworksPricingAndTrackSizeChange(
        oldArtwork.pricing.unitPrice,
        artwork.pricing.unitPrice
      );
    }
  }, [artwork]);

  return (
    <div
      {...restProps}
      className={cn("artwork-dimensions scope-6b9e2e48", restProps?.className, {
        "artwork-dimensions--with-border": showBorderUI(),
        "artwork-dimensions--with-border-invalid": isInvalid()
      })}
    >
      {label() && !showBorderUI() ? (
        <div className="artwork-dimensions__label scope-6b9e2e48">{label()}</div>
      ) : null}
      {showBorderUI() ? (
        <StepNumber
          className="scope-6b9e2e48"
          stepNumber={isPuff() ? 3 : 2}
          content="Set Design Size"
          data-testid="step-number"
        />
      ) : null}
      <div className="artwork-dimensions__form-number-row scope-6b9e2e48">
        <div className="artwork-dimensions__form-number-group scope-6b9e2e48">
          <label
            className={cn("artwork-dimensions__number-label scope-6b9e2e48", {
              "artwork-dimensions__number-label--gang": printAsIs()
            })}
            htmlFor={widthId()}
            data-testid="artwork-dimensions-number-label"
          >
            {" "}
            Width {!showBorderUI() ? <> (inches)</> : null}
          </label>
          <div
            className={cn("artwork-dimensions__number-control scope-6b9e2e48", {
              "artwork-dimensions__number-control--gang-lock": gangPadlock() && !!width()
            })}
          >
            <input
              className={cn("artwork-dimensions__number-field js-dtf-width-input scope-6b9e2e48", {
                "artwork-dimensions__number-field--invalid": validationError() && !isWidthValid
              })}
              id={widthId()}
              value={width()}
              max="100"
              min="0.1"
              name={widthId()}
              step=".01"
              type="number"
              data-hj-allow={true}
              disabled={disabled}
              readOnly={readonly || !!widthDefault}
              onClick={handleInputClick}
              onInput={handleWidthInput}
              onChange={handleWidthInput}
              onFocus={handleFocusIn}
              onBlur={onBlur}
              data-testid="artwork-dimensions-number-field"
            />
          </div>
        </div>
        <UseSvg
          className={cn("artwork-dimensions__multiplier scope-6b9e2e48", {
            "artwork-dimensions__multiplier--gang": printAsIs()
          })}
          icon="cross"
          data-testid="artwork-dimensions-multiplier"
        />

        <div className="artwork-dimensions__form-number-group scope-6b9e2e48">
          <label
            className={cn("artwork-dimensions__number-label scope-6b9e2e48", {
              "artwork-dimensions__number-label--gang": printAsIs()
            })}
            htmlFor={heightId()}
            data-testid="artwork-dimensions-number-label-1"
          >
            {heightLabel()}
          </label>
          <div
            className={cn("artwork-dimensions__number-control scope-6b9e2e48", {
              "artwork-dimensions__number-control--gang-lock": lockHeight()
            })}
          >
            <input
              className={cn("artwork-dimensions__number-field js-dtf-height-input scope-6b9e2e48", {
                "artwork-dimensions__number-field--invalid": validationError() && !isHeightValid
              })}
              id={heightId()}
              value={height()}
              max="100"
              min="0.1"
              name={heightId()}
              step=".01"
              type="number"
              data-hj-allow={true}
              disabled={disabled}
              readOnly={readonlyHeight()}
              onClick={handleInputClick}
              onInput={handleHeightInput}
              onChange={handleHeightInput}
              onFocus={handleFocusIn}
              onBlur={onBlur}
              data-testid="artwork-dimensions-number-field-1"
            />
          </div>
        </div>
        <UseSvg
          className="artwork-dimensions__multiplier scope-6b9e2e48"
          icon="cross"
          data-testid="artwork-dimensions-multiplier-1"
        />

        <div className="artwork-dimensions__form-number-group artwork-dimensions__form-number-group--price scope-6b9e2e48">
          {printAsIs() ? (
            <div className="artwork-dimensions__number-label artwork-dimensions__number-label--gang scope-6b9e2e48">
              {" "}
              Linear foot <br className="scope-6b9e2e48" /> price{" "}
            </div>
          ) : (
            <div
              className={cn("artwork-dimensions__number-label scope-6b9e2e48", {
                "artwork-dimensions__number-label--light": showBorderUI()
              })}
            >
              {" "}
              Price / in²{" "}
            </div>
          )}
          <div className="artwork-dimensions__static-input scope-6b9e2e48">
            {n(priceInch(), "currencyDetailed")}
          </div>
        </div>
        <div className="artwork-dimensions__equal-sign scope-6b9e2e48">=</div>
        <div className="artwork-dimensions__form-number-group artwork-dimensions__form-number-group--price scope-6b9e2e48">
          {printAsIs() ? (
            <div className="artwork-dimensions__number-label artwork-dimensions__number-label--gang scope-6b9e2e48">
              {" "}
              Price <br className="scope-6b9e2e48" /> per Gang Sheet{" "}
            </div>
          ) : (
            <div
              className={cn("artwork-dimensions__number-label scope-6b9e2e48", {
                "artwork-dimensions__number-label--light": showBorderUI()
              })}
            >
              {" "}
              Total price{" "}
            </div>
          )}
          <div className="artwork-dimensions__static-input scope-6b9e2e48">
            {n(totalPrice(), "currency")}
          </div>
        </div>
      </div>
      {showPaddingNote() ? (
        <div className="artwork-dimensions__free-note scope-6b9e2e48">{paddingNote()}</div>
      ) : null}
    </div>
  );
}
